import { dao } from '@platform/dao';
import type { ILinkTypeUsage, ITreeDeleteResult, ITreeNodeWrite, IWorkspaceModuleWrite, PathKind } from '@platform/dao';
import type { ICatalog, ITreeNode } from '@platform/core/types';
import { baseApi, run } from './baseApi';
import { moduleTag } from './moduleTag';
import { workspaceSettingTags } from './workspaceSettingTags';

const pathsTag = (ws: string, kind: PathKind) => ({ type: 'Paths' as const, id: `${kind}:${ws}` });

/**
 * «Налаштування» простору (прототип v4 cust-*): увімкнення, перейменування й скидання модулів (оновлює каталоги й меню; скидання видаляє
 * й перевизначення типів модуля — рішення 2 етапу 9), шляхи напрямів та ітерацій
 * (новий вузол, зміна, видалення гілки — записи переходять до батьківського вузла), видалення тегу, уживання типів зв'язків і
 * налаштування простору поверх платформних («Розміщення полів» простору; його зміна перечитує й глобальний каталог — workspaceSettingTags).
 */
export const customizeApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    updateWorkspaceModule: b.mutation<ICatalog, { ws: string; moduleKey: string; body: IWorkspaceModuleWrite }>({
      queryFn: ({ ws, moduleKey, body }) => run(() => dao.workspaces.updateModule(ws, moduleKey, body)),
      invalidatesTags: (_r, _e, { ws, moduleKey }) => ['Catalog', moduleTag(ws, moduleKey)],
    }),
    resetWorkspaceModule: b.mutation<void, { ws: string; moduleKey: string; recordType?: string }>({
      queryFn: ({ ws, moduleKey, recordType }) => run(() => dao.workspaces.resetModule(ws, moduleKey, recordType)),
      invalidatesTags: (_r, _e, { ws, moduleKey }) => ['Catalog', 'Override', moduleTag(ws, moduleKey)],
    }),
    createPathNode: b.mutation<ITreeNode, { ws: string; kind: PathKind; body: ITreeNodeWrite }>({
      queryFn: ({ ws, kind, body }) => run(() => dao.workspaces.createNode(ws, kind, body)),
      invalidatesTags: (_r, _e, { ws, kind }) => [pathsTag(ws, kind)],
    }),
    updatePathNode: b.mutation<ITreeNode, { ws: string; kind: PathKind; nodeId: string; body: ITreeNodeWrite }>({
      queryFn: ({ ws, kind, nodeId, body }) => run(() => dao.workspaces.updateNode(ws, kind, nodeId, body)),
      invalidatesTags: (_r, _e, { ws, kind }) => [pathsTag(ws, kind)],
    }),
    deletePathNode: b.mutation<ITreeDeleteResult, { ws: string; kind: PathKind; nodeId: string }>({
      queryFn: ({ ws, kind, nodeId }) => run(() => dao.workspaces.deleteNode(ws, kind, nodeId)),
      invalidatesTags: (_r, _e, { ws, kind }) => [pathsTag(ws, kind), 'RecordList', 'Record'],
    }),
    deleteTag: b.mutation<void, { ws: string; tagId: string }>({
      queryFn: ({ ws, tagId }) => run(() => dao.workspaces.deleteTag(ws, tagId)),
      invalidatesTags: (_r, _e, { ws }) => [{ type: 'Tags', id: ws }, 'RecordList', 'Record'],
    }),
    linkTypeUsage: b.query<ILinkTypeUsage[], string>({
      queryFn: (ws) => run(() => dao.workspaces.linkTypeUsage(ws)),
      providesTags: (_r, _e, ws) => [{ type: 'Links', id: `usage:${ws}` }],
    }),
    workspaceSettings: b.query<Record<string, unknown>, string>({ queryFn: (ws) => run(() => dao.workspaces.settings(ws)), providesTags: (_r, _e, ws) => workspaceSettingTags(ws) }),
    setWorkspaceSetting: b.mutation<Record<string, unknown>, { ws: string; key: string; value: unknown }>({
      queryFn: ({ ws, key, value }) => run(() => dao.workspaces.setSetting(ws, key, value)),
      invalidatesTags: (_r, _e, { ws, key }) => workspaceSettingTags(ws, key),
    }),
    resetWorkspaceSetting: b.mutation<Record<string, unknown>, { ws: string; key: string }>({
      queryFn: ({ ws, key }) => run(() => dao.workspaces.resetSetting(ws, key)),
      invalidatesTags: (_r, _e, { ws, key }) => workspaceSettingTags(ws, key),
    }),
  }),
});

export const {
  useUpdateWorkspaceModuleMutation, useResetWorkspaceModuleMutation, useCreatePathNodeMutation, useUpdatePathNodeMutation, useDeletePathNodeMutation, useDeleteTagMutation,
  useLinkTypeUsageQuery, useWorkspaceSettingsQuery, useSetWorkspaceSettingMutation, useResetWorkspaceSettingMutation,
} = customizeApi;
